import React from "react";
import InputField from "../../../../utils/useInputField";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiHome,
  FiMapPin,
  FiMap,
  FiExternalLink,
  FiGlobe,
} from "react-icons/fi";
import { FaSchool } from "react-icons/fa";

const NewsletterStatusBadge = ({ status, optedOut, t }) => {
  let label = t("parentPortal.newsletter.ineligible", { defaultValue: "Ineligible" });
  let className = "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
  let title = status || "Ineligible";

  if (optedOut) {
    label = t("parentPortal.newsletter.opted_out", { defaultValue: "Opted out" });
    className = "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
    title = t("parentPortal.newsletter.opted_out_hint", { defaultValue: "Parent opted out" });
  } else if (status === "SUBSCRIBED") {
    label = t("parentPortal.newsletter.subscribed", { defaultValue: "Subscribed" });
    className = "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    title = t("parentPortal.newsletter.subscribed_hint", { defaultValue: "Parent is subscribed" });
  } else if (status === "UNSUBSCRIBED") {
    label = t("parentPortal.newsletter.unsubscribed", { defaultValue: "Unsubscribed" });
    className = "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
    title = t("parentPortal.newsletter.unsubscribed_hint", { defaultValue: "Parent is eligible but not subscribed" });
  } else if (status && status !== "INELIGIBLE") {
    label = status;
    title = status;
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}
      title={title}
    >
      {label}
    </span>
  );
};

const getFirstAvailable = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const buildStreetLine = (school) =>
  [
    getFirstAvailable(school.address, school.street, school.streetAddress),
    [school.houseNumber, school.houseNumberAddition].filter(Boolean).join(""),
  ].filter(Boolean).join(" ");

const buildAddressLine = (address, zipCode, city) =>
  [address, [zipCode, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");

const hasSchoolData = (school) => {
  if (!school || typeof school !== "object") return false;
  const fields = [
    "schoolName",
    "name",
    "legalName",
    "brinNumber",
    "description",
    "phone",
    "email",
    "website",
    "street",
    "address",
    "postalCode",
    "zipCode",
    "city",
    "country",
    "logoUrl",
  ];
  return fields.some((field) => {
    const value = school[field];
    return value !== undefined && value !== null && String(value).trim() !== "";
  });
};

const buildMapsRouteUrl = ({ origin, destination }) => {
  const params = new URLSearchParams();

  if (origin) {
    params.set("origin", origin);
  }

  if (destination) {
    params.set("destination", destination);
  }

  params.set("travelmode", "driving");

  return `https://www.google.com/maps/dir/?api=1&${params.toString()}`;
};

const SchoolInfoRow = ({ icon, children }) => {
  if (!children) return null;

  return (
    <div className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-emerald-700 dark:text-emerald-300">
        {icon}
      </span>
      <span className="leading-relaxed">{children}</span>
    </div>
  );
};

const SectionDivider = () => (
  <div className="my-4 flex justify-center">
    <div className="h-[2px] w-11/12 rounded-full bg-gradient-to-r from-transparent via-emerald-200/90 to-transparent dark:via-emerald-700/70" />
  </div>
);

const SchoolDetailsCard = ({ schoolData, parent, t }) => {
  const school = schoolData || {};
  const name = getFirstAvailable(
    school.name,
    school.schoolName,
    school.organizationName,
    school.displayName
  );

  if (!hasSchoolData(school)) {
    return (
      <aside className="relative flex h-full flex-col border-t border-emerald-200/70 pt-5 dark:border-emerald-800/50 lg:border-t-0 lg:pl-10 lg:pt-0">
        <div className="absolute bottom-0 left-0 top-0 hidden items-center lg:flex">
          <div className="h-[70%] w-[2px] rounded-full bg-gradient-to-b from-transparent via-emerald-200/90 to-transparent dark:via-emerald-700/70" />
        </div>
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700 shadow-inner dark:bg-emerald-950/50 dark:text-emerald-200">
              <FaSchool className="h-7 w-7" aria-hidden="true" />
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {name}
              </h3>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("parentPortal.school.details_title", {
                  defaultValue: "School information",
                })}
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            {t("parentPortal.school.not_configured", {
              defaultValue: "School information not configured.",
            })}
          </p>
        </div>
      </aside>
    );
  }


  const address = buildStreetLine(school);
  const zipCode = getFirstAvailable(school.zipCode, school.postalCode, school.postcode);
  const city = getFirstAvailable(school.city, school.town, school.locality);
  const phone = getFirstAvailable(school.phone, school.phoneNumber, school.telephone);
  const email = getFirstAvailable(school.email, school.emailAddress);
  const website = getFirstAvailable(school.website, school.url);

  const schoolAddressLine = buildAddressLine(address, zipCode, city);
  const parentAddressLine = buildAddressLine(parent?.address, parent?.zipCode, parent?.city);
  const routeUrl = schoolAddressLine
    ? buildMapsRouteUrl({
        origin: parentAddressLine,
        destination: schoolAddressLine,
      })
    : null;

  const websiteUrl =
    website && /^https?:\/\//i.test(website) ? website : website ? `https://${website}` : "";

  return (
    <aside className="relative flex h-full flex-col border-t border-emerald-200/70 pt-5 dark:border-emerald-800/50 lg:border-t-0 lg:pl-10 lg:pt-0">
      <div className="absolute bottom-0 left-0 top-0 hidden items-center lg:flex">
        <div className="h-[70%] w-[2px] rounded-full bg-gradient-to-b from-transparent via-emerald-200/90 to-transparent dark:via-emerald-700/70" />
      </div>

      <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700 shadow-inner dark:bg-emerald-950/50 dark:text-emerald-200">
            <FaSchool className="h-7 w-7" aria-hidden="true" />
          </div>

          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {name}
            </h3>

          </div>
        </div>

        <div className="w-full max-w-sm space-y-2.5">
          {(address || zipCode || city) && (
            <SchoolInfoRow icon={<FiMapPin className="h-4 w-4" aria-hidden="true" />}>
              <>
                {address && <span className="block">{address}</span>}
                {(zipCode || city) && (
                  <span className="block">{[zipCode, city].filter(Boolean).join(" ")}</span>
                )}
              </>
            </SchoolInfoRow>
          )}

          <SchoolInfoRow icon={<FiPhone className="h-4 w-4" aria-hidden="true" />}>
            {phone}
          </SchoolInfoRow>

          <SchoolInfoRow icon={<FiMail className="h-4 w-4" aria-hidden="true" />}>
            {email}
          </SchoolInfoRow>

          {websiteUrl && (
            <SchoolInfoRow icon={<FiGlobe className="h-4 w-4" aria-hidden="true" />}>
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-700 hover:underline dark:text-emerald-300"
              >
                {website}
              </a>
            </SchoolInfoRow>
          )}
        </div>

        {routeUrl && (
          <a
            href={routeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-2xl border border-emerald-600/60 bg-white/40 px-4 py-2.5 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-50 dark:border-emerald-500/60 dark:bg-slate-950/20 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
          >
            <FiMapPin className="h-5 w-5" aria-hidden="true" />
            {t("parentPortal.school.route_to_school", { defaultValue: "Route to school" })}
            <FiExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        )}
      </div>
    </aside>
  );
};

const ParentInfoRow = ({ label, children }) => (
  <p className="grid gap-2 sm:grid-cols-[140px_1fr]">
    <strong>{label}:</strong>
    <span>{children}</span>
  </p>
);

const InfoTab = ({
  t,
  parentData,
  schoolData = null,
  schoolInfo = null,
  editMode,
  setEditMode,
  form,
  handleChange,
  handleSave,
  newsletterStatus,
  newsletterFirstSubscribed,
  isNewsletterEligible,
}) => {
  const safeParent = parentData || {};
  const safeSchool =
    schoolData ??
    schoolInfo ??
    safeParent.schoolSettings ??
    safeParent.school ??
    safeParent.schoolInfo ??
    safeParent.schoolDetails ??
    safeParent.schoolData ??
    {};

  return (
    <>
      {editMode ? (
        <>
          <div className="parent-glass-card space-y-4 text-left text-gray-900 dark:text-gray-100">
            <div className="space-y-2">
              <InputField icon={<FiUser />} name="firstName" value={form.firstName} onChange={handleChange} placeholder={t("parentPortal.name")} />
              <InputField icon={<FiUser />} name="lastName" value={form.lastName} onChange={handleChange} placeholder={t("parentPortal.last_name")} />
              <InputField icon={<FiMail />} name="email" type="email" value={form.email} onChange={handleChange} placeholder={t("parentPortal.email")} />
              <InputField icon={<FiPhone />} name="phone" value={form.phone} onChange={handleChange} placeholder={t("parentPortal.phone")} />
              <InputField icon={<FiHome />} name="address" value={form.address} onChange={handleChange} placeholder={t("parentPortal.address")} />
              <InputField icon={<FiMapPin />} name="zipCode" value={form.zipCode} onChange={handleChange} placeholder={t("parentPortal.zipcode")} />
              <InputField icon={<FiMap />} name="city" value={form.city} onChange={handleChange} placeholder={t("parentPortal.city")} />
            </div>

            <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {t("parentPortal.newsletter.status_label", { defaultValue: "Newsletter" })}:
                </span>
                <NewsletterStatusBadge status={newsletterStatus} optedOut={form.newsletterOptOut} t={t} />
              </div>

              {!form.newsletterOptOut && newsletterFirstSubscribed && (
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t("parentPortal.newsletter.first_subscribed", {
                    defaultValue: "First subscribed on {{date}}",
                    date: newsletterFirstSubscribed,
                  })}
                </p>
              )}

              {isNewsletterEligible && (
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("parentPortal.newsletter.opt_out_label", {
                        defaultValue: "Unsubscribe from newsletter",
                      })}
                    </span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t("parentPortal.newsletter.opt_out_hint", {
                        defaultValue: "If enabled, you will not receive newsletters, even if eligible.",
                      })}
                    </p>
                  </div>

                  <label className="inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="newsletterOptOut"
                      checked={!!form.newsletterOptOut}
                      onChange={handleChange}
                      className="sr-only peer"
                      role="switch"
                      aria-checked={!!form.newsletterOptOut}
                    />
                    <span className="relative flex h-6 w-11 items-center rounded-full bg-gray-300/60 transition peer-checked:bg-emerald-500/80 peer-checked:shadow-[0_0_12px_rgba(16,185,129,0.45)] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-400/70 dark:bg-gray-600/60">
                      <span className="absolute left-[4px] top-[4px] h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-[20px]" />
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-4">
            <button onClick={() => setEditMode(false)} className="parent-form-button parent-form-button-reset">
              {t("common.cancel")}
            </button>
            <button onClick={handleSave} className="parent-form-button parent-form-button-submit">
              {t("common.save")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="parent-glass-card text-left text-gray-900 dark:text-gray-100">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
              <div className="space-y-5">
                <section className="space-y-2">
                  <ParentInfoRow label={t("parentPortal.name")}>
                    {safeParent.firstName} {safeParent.lastName}
                  </ParentInfoRow>
                  <ParentInfoRow label={t("parentPortal.phone")}>
                    {safeParent.phone || t("parentPortal.no_phone")}
                  </ParentInfoRow>
                  <ParentInfoRow label={t("parentPortal.email")}>
                    {safeParent.email}
                  </ParentInfoRow>
                </section>

                <SectionDivider />

                <section className="space-y-2">
                  <ParentInfoRow label={t("parentPortal.address")}>
                    {safeParent.address || t("parentPortal.no_address")}
                  </ParentInfoRow>
                  <ParentInfoRow label={t("parentPortal.zipcode")}>
                    {safeParent.zipCode || t("parentPortal.no_zipcode")}
                  </ParentInfoRow>
                  <ParentInfoRow label={t("parentPortal.city")}>
                    {safeParent.city || t("parentPortal.no_city")}
                  </ParentInfoRow>
                </section>

                <SectionDivider />

                <section>
                  <p className="flex flex-wrap items-center gap-2">
                    <strong>{t("parentPortal.newsletter.status_label", { defaultValue: "Newsletter" })}:</strong>
                    <NewsletterStatusBadge
                      status={safeParent.newsletterStatus}
                      optedOut={safeParent.newsletterOptOut}
                      t={t}
                    />
                  </p>

                  {safeParent.newsletterOptOut ? (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t("parentPortal.newsletter.opted_out_message", {
                        defaultValue: "You have opted out of the newsletter.",
                      })}
                    </p>
                  ) : (
                    newsletterFirstSubscribed && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t("parentPortal.newsletter.first_subscribed", {
                          defaultValue: "First subscribed on {{date}}",
                          date: newsletterFirstSubscribed,
                        })}
                      </p>
                    )
                  )}
                </section>
              </div>

              <SchoolDetailsCard schoolData={safeSchool} parent={safeParent} t={t} />
            </div>
          </div>

          <button onClick={() => setEditMode(true)} className="mt-4 parent-form-button parent-form-button-submit">
            {t("parentPortal.buttons.edit")}
          </button>
        </>
      )}
    </>
  );
};

export default InfoTab;
